import type CreativeSDK from '@cesdk/cesdk-js';
import { GQLProductSideAvailableDecorationType } from '@theprintbar/graphql';
import {
	Pantone,
	extractSvgColors,
	getImageData,
	getPantoneColours,
	pixelToPantone,
	replaceSVGColours,
	svgToDataUrl,
} from '@theprintbar/shared';
import { getColours } from './colours';
import { MimeType } from './types';

export function getCurrentDecorationMethod(editor: CreativeSDK | null): GQLProductSideAvailableDecorationType {
	if (!editor) throw 'Editor not found';

	const { block, scene } = editor.engine;

	const page = scene.getCurrentPage();
	if (!page) throw 'Unable to find current page';

	return block.hasMetadata(page, 'decorationMethod')
		? (block.getMetadata(page, 'decorationMethod') as GQLProductSideAvailableDecorationType)
		: GQLProductSideAvailableDecorationType.Dtg;
}

export function setDecorationMethod(editor: CreativeSDK | null, method: GQLProductSideAvailableDecorationType) {
	if (!editor) throw 'Editor not found';

	const { block, scene } = editor.engine;

	const page = scene.getCurrentPage();
	if (!page) throw 'Unable to find current page';

	block.setMetadata(page, 'decorationMethod', method);
}

export function getOriginalFill(editor: CreativeSDK, fill: number) {
	const { block } = editor.engine;

	const sourcSet = block.getSourceSet(fill, 'fill/image/sourceSet');
	const originalSet = sourcSet?.find(set => !set.uri.includes('size=')) || sourcSet[0];
	let src = originalSet?.uri;
	if (block.hasMetadata(fill, 'originalFill')) {
		src = block.getMetadata(fill, 'originalFill');
	} else {
		block.setMetadata(fill, 'originalFill', src);
	}

	return src;
}

export async function getMetaColours(editor: CreativeSDK, block: number) {
	const { block: blockEngine } = editor.engine;

	if (blockEngine.hasMetadata(block, 'colours')) {
		return JSON.parse(blockEngine.getMetadata(block, 'colours')) as Pantone[];
	}

	//Add the colours to the block
	try {
		let colours: Pantone[] = [];
		const pantones = await getPantoneColours();
		const fill = blockEngine.getFill(block);
		const sourcSet = blockEngine.getSourceSet(fill, 'fill/image/sourceSet');
		const originalSet = sourcSet?.find(set => !set.uri.includes('size=')) || sourcSet[0];
		if (!originalSet) return [];
		const { uri } = originalSet;

		if (uri.includes('.svg')) {
			//Fetch the SVG and check if it's a vector
			const svgResponse = await fetch(uri);
			const svg = await svgResponse.text();
			//Get the SVG colours
			colours = extractSvgColors(svg).map(pixel => pixelToPantone(pixel, pantones));

			const dec = getCurrentDecorationMethod(editor);
			if (dec === GQLProductSideAvailableDecorationType.Scr || dec === GQLProductSideAvailableDecorationType.Emb) {
				const newSVG = replaceSVGColours(svg, {}, colours);
				blockEngine.setMetadata(fill, 'originalFill', uri);
				blockEngine.setSourceSet(fill, 'fill/image/sourceSet', [
					{
						width: blockEngine.getWidth(block),
						height: blockEngine.getHeight(block),
						uri: svgToDataUrl(newSVG),
					},
				]);
			}
		} else {
			const blob = await blockEngine.export(block, MimeType.Png, { targetWidth: 250, targetHeight: 250 });
			if (!blob.size) {
				console.log('No image size');
			} else {
				const imageData = await getImageData(blob);
				colours = await getColours(imageData.data);
			}
		}
		blockEngine.setMetadata(block, 'colours', JSON.stringify(colours));
		return colours;
	} catch (e) {
		console.log(e);
	}

	return [];
}
